import { z } from 'zod'
import { requestEnvelopeV2 } from './client'
import {
  auditLogSchema, nullableArray, paginated, permissionSchema, roleSchema, v2UserSchema,
  volunteerAgreementSchema, volunteerApplicationSchema, volunteerCertificatesPayloadSchema, volunteerTaskSchema,
  type AuditLog, type PermissionRecord, type Role, type V2User,
  type VolunteerAgreement, type VolunteerApplication, type VolunteerCertificatesPayload, type VolunteerTask,
} from './schemas'
import type { AuditFilter, TaskFilter, UserFilter, VolunteerFilter } from '../lib/query-keys'

// ---------- 会话 ----------

export async function getMe(): Promise<V2User> {
  return requestEnvelopeV2('/me', v2UserSchema)
}

// ---------- 用户 / 角色 / 权限 / 审计 ----------

export async function listUsers(f: UserFilter) {
  const params = new URLSearchParams({ page: String(f.page), pageSize: String(f.pageSize) })
  if (f.regionCode) params.set('regionCode', f.regionCode)
  return requestEnvelopeV2(`/admin/users?${params.toString()}`, paginated(v2UserSchema))
}

export async function listRoles(): Promise<Role[]> {
  return requestEnvelopeV2('/admin/roles', nullableArray(roleSchema))
}

export async function listPermissions(): Promise<PermissionRecord[]> {
  return requestEnvelopeV2('/admin/permissions', nullableArray(permissionSchema))
}

export async function listAuditLogs(f: AuditFilter) {
  const params = new URLSearchParams({ page: String(f.page), pageSize: String(f.pageSize) })
  if (f.actorId) params.set('actorId', f.actorId)
  if (f.action) params.set('action', f.action)
  if (f.regionCode) params.set('regionCode', f.regionCode)
  const data = await requestEnvelopeV2(`/admin/audit?${params.toString()}`, paginated(auditLogSchema))
  return data
}

// ---------- 志愿者申请 ----------

export async function listVolunteerApplications(f: VolunteerFilter) {
  const params = new URLSearchParams({ page: String(f.page), pageSize: String(f.pageSize) })
  if (f.regionCode) params.set('regionCode', f.regionCode)
  if (f.status) params.set('status', f.status)
  return requestEnvelopeV2(`/volunteer/applications?${params.toString()}`, paginated(volunteerApplicationSchema))
}

export async function reviewVolunteerApplication(
  id: string,
  body: { status: 'approved' | 'rejected'; note?: string },
): Promise<void> {
  await requestEnvelopeV2(
    `/volunteer/applications/${encodeURIComponent(id)}/review`,
    z.object({}).passthrough(),
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  )
}

// ---------- 任务调度 ----------

export async function listTasks(f: TaskFilter) {
  const params = new URLSearchParams({ page: String(f.page), pageSize: String(f.pageSize) })
  if (f.regionCode) params.set('regionCode', f.regionCode)
  if (f.status) params.set('status', f.status)
  if (f.volunteerId) params.set('volunteerId', f.volunteerId)
  return requestEnvelopeV2(`/tasks?${params.toString()}`, paginated(volunteerTaskSchema))
}

export async function createTask(body: {
  eventId: string
  type: string
  lat?: number
  lon?: number
  radiusKm?: number
  requiredCapability?: string
  regionCode?: string
}): Promise<VolunteerTask> {
  return requestEnvelopeV2('/tasks', volunteerTaskSchema, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function updateTaskStatus(
  id: string,
  body: { status: 'pending' | 'assigned' | 'accepted' | 'done' | 'timeout'; note?: string },
): Promise<void> {
  await requestEnvelopeV2(
    `/tasks/${encodeURIComponent(id)}/status`,
    z.record(z.string(), z.unknown()),
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  )
}

// ---------- 证书发放 / 协议 ----------

/** 证书发放记录（只读，需 tasks.read 权限；data 形状宽松解析，汇总字段可选） */
export async function listVolunteerCertificates(): Promise<VolunteerCertificatesPayload> {
  return requestEnvelopeV2('/volunteer/certificates', volunteerCertificatesPayloadSchema)
}

/** 志愿者服务协议当前版本（公开端点） */
export async function getVolunteerAgreement(): Promise<VolunteerAgreement> {
  return requestEnvelopeV2('/volunteer/agreement', volunteerAgreementSchema)
}

export type { AuditLog, PermissionRecord, Role, V2User, VolunteerApplication, VolunteerTask }
